/**
 * IntakeForm Component
 * Form for adding food to a meal category.
 *
 * Search combobox behaviour:
 *  1. Pantry items are listed first, in a green-tinted group.
 *  2. A separator line follows.
 *  3. Remaining foods are grouped by their food category (kategoriaNazov).
 *     An "Uncategorised" group catches foods with no category.
 *  4. When a search term ≥2 chars is entered, the server is queried for
 *     matching foods (food table only — recipes are added via the recipe picker).
 *
 * Amount entry:
 *  - Two linked inputs: grams and kcal.
 *  - Editing one recalculates the other in real time.
 */

import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, ShoppingBasket } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { calculateNutrition } from "../utils";
import type { Potravina } from "../types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SelectedItem = { type: "potravina"; food: Potravina };

interface IntakeFormProps {
  potraviny: Potravina[];
  onSubmit: (
    potravinaId: number | null,
    receptId: number | null,
    amount: string,
    notes: string
  ) => Promise<void>;
  onCancel: () => void;
  onPreviewChange?: (potravinaId: number | null, amount: string) => void;
  /** klientId is needed to load the client's pantry for prioritised display */
  klientId?: number | null;
}

const MAX_PER_GROUP = 40;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IntakeForm({
  potraviny,
  onSubmit,
  onCancel,
  onPreviewChange,
  klientId,
}: IntakeFormProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SelectedItem | null>(null);
  const [amount, setAmount] = useState("100");
  const [kcalInput, setKcalInput] = useState("");
  const [notes, setNotes] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  // Fetch the client's pantry availability (with amounts) so we can colour foods
  const { data: pantryAvailability = [] } =
    trpc.spajza.getAvailability.useQuery(
      { klientId: klientId! },
      { enabled: !!klientId }
    );

  // Server-side food search — fires when search term is non-empty
  const { data: searchFoodResults = [] } = trpc.potravina.listAll.useQuery(
    { search: searchValue },
    { enabled: searchValue.length >= 2 }
  );

  // Map of potravinaId -> available grams
  const pantryMap = useMemo(
    () => new Map(pantryAvailability.map(a => [a.potravinaId, a.dostupneG])),
    [pantryAvailability]
  );
  const pantryIds = useMemo(
    () => new Set(pantryAvailability.map(a => a.potravinaId)),
    [pantryAvailability]
  );

  // kcal per 100g for the currently selected item
  const kcalPer100g = useMemo((): number => {
    if (!selected) return 0;
    const p = selected.food;
    return p.energiaKcal
      ? parseFloat(p.energiaKcal)
      : p.energiaKj
        ? parseFloat(p.energiaKj) / 4.18
        : 0;
  }, [selected]);

  // Keep grams and kcal in sync
  const handleAmountChange = useCallback(
    (grams: string) => {
      setAmount(grams);
      if (kcalPer100g > 0 && grams) {
        const kcal = (parseFloat(grams) * kcalPer100g) / 100;
        setKcalInput(isNaN(kcal) ? "" : kcal.toFixed(1));
      } else {
        setKcalInput("");
      }
      if (selected?.type === "potravina") {
        onPreviewChange?.(selected.food.id ?? null, grams);
      }
    },
    [kcalPer100g, selected, onPreviewChange]
  );

  const handleKcalChange = useCallback(
    (kcalStr: string) => {
      setKcalInput(kcalStr);
      if (kcalPer100g > 0 && kcalStr) {
        const grams = (parseFloat(kcalStr) / kcalPer100g) * 100;
        const gramsStr = isNaN(grams) ? "" : grams.toFixed(1);
        setAmount(gramsStr);
        if (selected?.type === "potravina") {
          onPreviewChange?.(selected.food.id ?? null, gramsStr);
        }
      }
    },
    [kcalPer100g, selected, onPreviewChange]
  );

  const handleSelect = (item: SelectedItem) => {
    setSelected(item);
    const defaultGrams = "100";
    setAmount(defaultGrams);
    const kcal100 = item.food.energiaKcal
      ? parseFloat(item.food.energiaKcal)
      : item.food.energiaKj
        ? parseFloat(item.food.energiaKj) / 4.18
        : 0;
    setKcalInput(kcal100 > 0 ? kcal100.toFixed(1) : "");
    onPreviewChange?.(item.food.id ?? null, defaultGrams);
    setSearchOpen(false);
  };

  const handleSubmit = async () => {
    if (!selected) return;
    const potravinaId = selected.food.id ?? null;
    await onSubmit(potravinaId, null, amount, notes);
    setSelected(null);
    setAmount("100");
    setKcalInput("");
    setNotes("");
    setSearchValue("");
  };

  // Nutrition preview
  const selectedPotravina = selected?.food ?? null;
  const nutrition = selectedPotravina
    ? calculateNutrition(selectedPotravina, amount)
    : null;

  /**
   * Split the food list into pantry items and categorised groups.
   * When a search term is active (≥2 chars), use the server search results.
   */
  const { pantryItems, groupedOther } = useMemo(() => {
    // When searching, show server search results
    if (searchValue.length >= 2) {
      const pantry: Potravina[] = [];
      const other: Potravina[] = [];
      for (const f of searchFoodResults) {
        if (f.id !== undefined && pantryIds.has(f.id))
          pantry.push(f as Potravina);
        else other.push(f as Potravina);
      }
      return {
        pantryItems: pantry,
        groupedOther: other.length
          ? [
              {
                category: t("intake:table.searchResults", "Search results"),
                items: other,
              },
            ]
          : [],
      };
    }

    // Default: show all foods grouped by category
    const pantry: Potravina[] = [];
    const byCategory: Record<string, Potravina[]> = {};
    for (const p of potraviny) {
      if (p.id !== undefined && pantryIds.has(p.id)) {
        pantry.push(p);
      } else {
        const cat =
          p.kategoriaNazov ||
          p.kategoria ||
          t("intake:table.uncategorised", "Uncategorised");
        if (!byCategory[cat]) byCategory[cat] = [];
        byCategory[cat].push(p);
      }
    }
    const uncategorised = t("intake:table.uncategorised", "Uncategorised");
    const sortedCategories = Object.keys(byCategory).sort((a, b) => {
      if (a === uncategorised) return 1;
      if (b === uncategorised) return -1;
      return a.localeCompare(b);
    });
    const grouped = sortedCategories.map(cat => ({
      category: cat,
      items: byCategory[cat].slice(0, MAX_PER_GROUP),
    }));
    return {
      pantryItems: pantry.slice(0, 30),
      groupedOther: grouped,
    };
  }, [potraviny, pantryIds, searchValue, searchFoodResults, t]);

  const selectedLabel = selected?.food.nazov ?? t("intake:table.selectFood");

  return (
    <Card className="mb-4 border-primary">
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ── Food search ── */}
          <div className="space-y-2 md:col-span-2">
            <Label>{t("intake:table.selectFood")} *</Label>
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2 truncate">
                    {selectedLabel}
                  </span>
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[480px] p-0" align="start">
                <Command>
                  <CommandInput
                    placeholder={t("intake:table.selectFood")}
                    value={searchValue}
                    onValueChange={setSearchValue}
                  />
                  <CommandList className="max-h-80 overflow-y-auto">
                    <CommandEmpty>{t("intake:table.noFoodFound")}</CommandEmpty>

                    {/* ── Pantry group ── */}
                    {pantryItems.length > 0 && (
                      <CommandGroup
                        heading={
                          <span className="flex items-center gap-1 text-green-700 dark:text-green-400">
                            <ShoppingBasket className="h-3 w-3" />
                            {t("mealLibrary:spajza.title")}
                          </span>
                        }
                      >
                        {pantryItems.map(p => {
                          const availG =
                            p.id !== undefined ? (pantryMap.get(p.id) ?? 0) : 0;
                          const badgeCls =
                            availG > 0
                              ? "text-xs text-green-700 border-green-400 bg-green-50 dark:bg-green-950/40 dark:text-green-300"
                              : "text-xs text-yellow-700 border-yellow-400 bg-yellow-50 dark:bg-yellow-950/40";
                          return (
                            <CommandItem
                              key={`pantry-${p.id}`}
                              value={`pantry-${p.nazov}`}
                              className="bg-green-50 aria-selected:bg-green-100 dark:bg-green-950/30 dark:aria-selected:bg-green-900/50"
                              onSelect={() =>
                                handleSelect({ type: "potravina", food: p })
                              }
                            >
                              <span className="flex-1 truncate">{p.nazov}</span>
                              <Badge variant="outline" className={badgeCls}>
                                {availG >= 1000
                                  ? `${(availG / 1000).toFixed(2)} kg`
                                  : `${availG.toFixed(0)} g`}
                              </Badge>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}

                    {pantryItems.length > 0 && groupedOther.length > 0 && (
                      <CommandSeparator />
                    )}

                    {/* ── Category groups ── */}
                    {groupedOther.map(({ category, items }) => (
                      <CommandGroup key={category} heading={category}>
                        {items.map(p => (
                          <CommandItem
                            key={p.id ?? p.nazov}
                            value={p.nazov}
                            onSelect={() =>
                              handleSelect({ type: "potravina", food: p })
                            }
                          >
                            {p.nazov}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* ── Amount: grams ── */}
          <div className="space-y-2">
            <Label>{t("intake:table.amount")} *</Label>
            <Input
              type="number"
              value={amount}
              onChange={e => handleAmountChange(e.target.value)}
              placeholder="e.g., 100"
              step="0.1"
              min="0.1"
            />
          </div>

          {/* ── Amount: kcal ── */}
          <div className="space-y-2">
            <Label>Energy (kcal)</Label>
            <Input
              type="number"
              value={kcalInput}
              onChange={e => handleKcalChange(e.target.value)}
              placeholder="e.g., 200"
              step="1"
              min="0"
              disabled={!selected}
            />
          </div>

          {/* ── Notes ── */}
          <div className="md:col-span-2 space-y-2">
            <Label>{t("intake:table.notesOptional")}</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={t("intake:table.notesOptional")}
              className="min-h-[60px]"
            />
          </div>

          {/* ── Nutrition preview ── */}
          {nutrition && (
            <div className="md:col-span-2">
              <div className="rounded-lg bg-background p-4 border">
                <div className="text-sm font-medium mb-2">
                  {t("intake:dialog.previewNutrition")} {amount}g:
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-amber-600">
                      {nutrition.energiaKcal}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("intake:dialog.kcal")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-600">
                      {nutrition.proteinG}g
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("intake:dialog.protein")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-orange-600">
                      {nutrition.tukG}g
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("intake:dialog.fat")}
                    </div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-teal-600">
                      {nutrition.sacharidG}g
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("intake:dialog.carbs")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel}>
              {t("intake:dialog.cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={!selected}>
              {t("intake:dialog.done")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
